import { FileText, Sprout } from "lucide-react";

const SECTIONS = [
  {
    id: "acceptance",
    title: "1. Acceptance of these terms",
    body: `By accessing or using Matt's Daily Savers ("the site") you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, please do not use the site. These terms are governed by the laws of England and Wales.`,
  },
  {
    id: "who-we-are",
    title: "2. Who we are",
    body: `Matt's Daily Savers is a personal finance blog and directory operated by Matt (a UK sole trader). The site publishes reviews, comparisons and guides on cashback sites, reward apps, card tools and survey panels that we personally use.`,
  },
  {
    id: "not-financial-advice",
    title: "3. Content is for information only — not financial advice",
    body: `All content on this site is provided for general informational and educational purposes only and does not constitute financial, tax, legal, investment or professional advice.

• We are not authorised or regulated by the Financial Conduct Authority (FCA).
• Nothing on this site should be taken as a personal recommendation to buy, sell, subscribe to or use any financial product or service.
• Every financial decision is personal. What works for us may not be suitable for your circumstances.
• You should always do your own research and, where appropriate, speak to a qualified independent financial adviser before making any financial decision.

We accept no liability for any loss (financial or otherwise) arising from decisions made in reliance on content published here.`,
  },
  {
    id: "accuracy",
    title: "4. Accuracy of information",
    body: `We take reasonable care to ensure the information on this site is accurate and up to date at the time of publication. However:

• Offers, cashback rates, bonuses, terms, fees and features of third-party services change frequently and often without notice.
• Screenshots, figures and monthly earning estimates reflect our own experience and are not guarantees of results.
• You are responsible for checking the current terms directly with any third-party provider before signing up or making a purchase.

We do not warrant that the site is free from errors, omissions or interruptions.`,
  },
  {
    id: "affiliate",
    title: "5. Referral and affiliate links",
    body: `Many outbound links on this site are referral or affiliate links. When you click one and sign up to (or purchase from) a third-party service, we may receive a commission or bonus at no additional cost to you. Where a link is a referral or affiliate link, this is disclosed on the relevant page and reinforced sitewide.

Our editorial approach:

• We only recommend services we have personally used.
• Our opinions are our own — we are not paid to write positive reviews.
• Commissions help fund the running of the site but do not affect our rankings or recommendations.`,
  },
  {
    id: "third-party",
    title: "6. Third-party websites and services",
    body: `The site contains links to third-party websites, apps and services. These are provided for your convenience. We do not control, endorse or accept responsibility for the content, availability, terms, privacy practices or actions of any third-party site or service. Your dealings with any third party are strictly between you and them.`,
  },
  {
    id: "acceptable-use",
    title: "7. Acceptable use",
    body: `You agree that you will not:

• Use the site for any unlawful, fraudulent or malicious purpose.
• Attempt to gain unauthorised access to the site, its servers, its admin area or any related systems.
• Scrape, spider or copy substantial portions of the site's content without prior written permission.
• Introduce viruses, trojans, worms or other malicious material.
• Impersonate any person or misrepresent your identity or affiliation.`,
  },
  {
    id: "intellectual-property",
    title: "8. Intellectual property",
    body: `All content on this site — including text, images, logos, layouts and code — is owned by Matt's Daily Savers or licensed for use, and is protected by UK and international copyright and other intellectual-property laws. You may view, download and print pages for your own personal, non-commercial use only. Any other use requires our prior written consent.`,
  },
  {
    id: "liability",
    title: "9. Limitation of liability",
    body: `To the fullest extent permitted by law:

• The site is provided on an "as is" and "as available" basis, without warranties of any kind, express or implied.
• We will not be liable for any indirect, incidental, special, consequential or punitive loss, or any loss of profits, revenue, data, savings or goodwill, arising out of or in connection with your use of the site.
• Nothing in these terms excludes or limits our liability for death or personal injury caused by negligence, for fraud, or for any other liability that cannot be excluded under UK law.`,
  },
  {
    id: "changes",
    title: "10. Changes to these terms",
    body: `We may amend these Terms of Service at any time by posting a revised version on this page. Continued use of the site after changes are posted constitutes acceptance of the revised terms.`,
  },
  {
    id: "governing-law",
    title: "11. Governing law",
    body: `These terms and any dispute arising from them are governed by the laws of England and Wales and subject to the exclusive jurisdiction of the courts of England and Wales.`,
  },
  {
    id: "contact",
    title: "12. Contact",
    body: `Questions about these terms? Please get in touch via the Contact page.`,
  },
];

export default function Terms() {
  const updated = "December 2025";
  return (
    <main data-testid="terms-page">
      <section className="mds-container pt-14 pb-6">
        <span className="mds-badge mds-badge-primary"><FileText size={14} /> Legal</span>
        <h1 className="font-heading font-semibold text-4xl sm:text-5xl mt-4 tracking-tight leading-tight">Terms of Service</h1>
        <p className="text-[#4A5D54] mt-3">Last updated: {updated}</p>
      </section>

      <section className="mds-container pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start hidden lg:block">
          <div className="mds-card">
            <div className="flex items-center gap-2 mb-4">
              <Sprout size={18} className="text-[#6B8F73]" />
              <h3 className="font-heading font-semibold">On this page</h3>
            </div>
            <ul className="space-y-2 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.id}><a href={`#${s.id}`} className="mds-link">{s.title}</a></li>
              ))}
            </ul>
          </div>
        </aside>

        <article className="lg:col-span-8 space-y-8 mds-prose text-base">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24" data-testid={`terms-${s.id}`}>
              <h2 className="font-heading font-semibold text-2xl text-[#1C2321] mb-3">{s.title}</h2>
              <div className="whitespace-pre-line">{s.body}</div>
            </section>
          ))}
        </article>
      </section>
    </main>
  );
}
